import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/shared/utils/axios.ts';
import { capitalize } from '@/shared/utils/format.ts';
import { useAppDispatch, useAppSelector } from '@/app';
import { setUser } from '@/features/auth';
import { LayoutConfig, PageConfig, User } from '@/entities/user';
import type { CreatePartnerPayload } from '@/entities/partner';

interface UserLayoutResponse extends Partial<User> {
  layout: LayoutConfig;
}

export const useLayout = (enabled: boolean = true, userId?: string) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth?.user);
  const skipLayoutRefetch = useAppSelector(
    state => state.auth?.skipLayoutRefetch
  );

  const {
    data: layout,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['user.layout', userId],
    queryFn: async (): Promise<LayoutConfig> => {
      const res = await apiClient.get<UserLayoutResponse>(
        '/dooh_api/user/v1/layout'
      );

      if (res.success !== true) {
        throw new Error(res.message || 'Failed to fetch user layout');
      }

      const userResp = res.data;
      if (!userResp.layout) {
        throw new Error('User layout was not returned');
      }

      dispatch(setUser({ ...user, ...userResp } as User));

      const retargetingCampaignPage: PageConfig = {
        title: 'Retargeting Campaigns',
        widgets: [
          {
            order: 1,
            widgets: [
              {
                id: 'RetargetingCampaigns',
                props: {
                  flex: 1,
                  title: 'Retargeting Campaigns',
                },
              },
            ],
          },
        ],
      };

      const retargetingCampaignNewPage: PageConfig = {
        title: 'New Retargeting Campaign',
        widgets: [
          {
            order: 1,
            widgets: [
              {
                id: 'RetargetingCampaignNew',
                props: {
                  flex: 1,
                  title: 'New Retargeting Campaign',
                },
              },
            ],
          },
        ],
      };

      const viewRetargetingCampaignPage: PageConfig = {
        title: 'Retargeting Campaign Info',
        widgets: [
          {
            order: 1,
            widgets: [
              {
                id: 'ViewRetargetingCampaign',
                props: {
                  flex: 1,
                  title: 'Retargeting Campaign Info',
                },
              },
            ],
          },
        ],
      };

      const baseLayout = userResp.layout as LayoutConfig & {
        routes?: string[];
      };

      return {
        ...baseLayout,
        retargeting_campaign:
          baseLayout.retargeting_campaign ?? retargetingCampaignPage,
        retargeting_campaign_new:
          baseLayout.retargeting_campaign_new ?? retargetingCampaignNewPage,
        view_retargeting_campaign:
          baseLayout.view_retargeting_campaign ?? viewRetargetingCampaignPage,
        routes: Array.from(
          new Set([
            ...(baseLayout.routes ?? []),
            'retargeting_campaign',
            'retargeting_campaign_new',
            'view_retargeting_campaign',
          ])
        ),
      } as LayoutConfig;
    },
    enabled: enabled && !!userId && !skipLayoutRefetch,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  return {
    layout,
    isLoading,
    isError,
    error,
  };
};

export const getPartners = () => {
  const {
    data: partners,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const res = await apiClient.get<string[]>(
        '/sdk_api/v1/admin-user/getPartnerIds'
      );

      if (res.status === 'success') {
        return res?.data.map((partnerId: string) => ({
          id: partnerId,
          name: capitalize(partnerId, { mode: 'proper' }),
          role: '',
        }));
      }

      return [{ id: '', name: '', role: '' }];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  return {
    partners,
    loading: isLoading,
    isError,
    error: error?.message,
  };
};

export const setPartner = () => {
  const {
    mutate: handlePartnerChange,
    isError,
    error,
    isPending,
  } = useMutation({
    mutationFn: async (partnerId: string): Promise<void> => {
      await apiClient.get(
        `/sdk_api/v1/admin-user/changePartner?partner_id=${partnerId}`
      );

      location.reload();
    },
  });

  return {
    handlePartnerChange,
    isError,
    error,
    isPending,
  };
};

export const useCreatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePartnerPayload) => {
      const res = await apiClient.post(
        '/dooh_api/partner/v1/partner/create',
        payload
      );

      if (!res.success) {
        throw new Error(res.message || 'Failed to create partner');
      }

      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
};
